/**
 * Servicio NFC
 * Monedero Digital NFC
 * 
 * Maneja todas las operaciones de lectura/escritura NFC
 */

import encryptionService from './encryptionService';
import mockDataService from './mockDataService';
import { ERROR_TYPES, NFC_CONFIG, APP_CONFIG } from '../utils/constants';
import { sleep } from '../utils/helpers';

class NFCService {
  constructor() {
    this.isScanning = false;
    this.isWriting = false;
    this.reader = null;
    this.simulatorEnabled = APP_CONFIG.ENABLE_SIMULATOR;
    
    // Tarjetas simuladas para desarrollo
    this.simulatedCards = [
      {
        serialNumber: '04:A1:B2:C3:D4:E5:F6',
        estudianteId: 'est_12345',
        nombre: 'Juan Pérez',
        saldo: 20000
      },
      {
        serialNumber: '04:B2:C3:D4:E5:F6:A1',
        estudianteId: 'est_12346',
        nombre: 'María González',
        saldo: 15000
      },
      {
        serialNumber: '04:C3:D4:E5:F6:A1:B2',
        estudianteId: 'est_12347',
        nombre: 'Carlos Rodríguez',
        saldo: 25000
      },
      {
        serialNumber: '04:D4:E5:F6:A1:B2:C3',
        estudianteId: 'est_12348',
        nombre: 'Ana Martínez',
        saldo: 8000
      }
    ];
  }

  /**
   * Verifica si NFC está soportado por el navegador
   */
  isNFCSupported() {
    if (this.simulatorEnabled) {
      console.log('🔧 Modo simulador NFC activado');
      return true;
    }
    
    return 'NDEFReader' in window;
  }

  /**
   * Escanea una tarjeta NFC
   */
  async scan(onReading, onError) {
    try {
      if (!this.isNFCSupported()) {
        throw new Error(ERROR_TYPES.NFC_NOT_SUPPORTED);
      }

      // Si estamos en modo simulador
      if (this.simulatorEnabled) {
        return await this.simulateScan(onReading);
      }

      this.isScanning = true;
      this.reader = new window.NDEFReader();
      
      await this.reader.scan();
      console.log('✅ Escaneo NFC iniciado');

      // Evento de lectura exitosa
      this.reader.onreading = (event) => {
        console.log('📱 Tarjeta NFC detectada');
        this.handleReading(event, onReading);
      };

      // Evento de error de lectura
      this.reader.onreadingerror = (error) => {
        console.error('❌ Error leyendo NFC:', error);
        this.isScanning = false;
        
        if (onError) {
          onError(new Error(ERROR_TYPES.NFC_READ_ERROR));
        }
      };

      return { success: true };
    } catch (error) {
      console.error('❌ Error iniciando escaneo NFC:', error);
      this.isScanning = false;

      if (error.name === 'NotAllowedError') {
        throw new Error(ERROR_TYPES.NFC_PERMISSION_DENIED);
      }

      throw new Error(ERROR_TYPES.NFC_READ_ERROR);
    }
  }

  /**
   * Simula un escaneo NFC (para desarrollo sin hardware NFC)
   */
  async simulateScan(onReading) {
    console.log('🔧 Simulando escaneo NFC...');
    console.log('📱 Por favor, acerca un carnet NFC al lector...');
    
    // Espera un poco para simular el proceso de escaneo
    await sleep(1500);

    // Selecciona una tarjeta simulada aleatoria
    const randomIndex = Math.floor(Math.random() * this.simulatedCards.length);
    const simulatedCard = this.simulatedCards[randomIndex];

    console.log('📱 Tarjeta NFC detectada:', simulatedCard.nombre);

    // Busca los datos reales del estudiante en mock data
    const student = mockDataService.getStudentByNFC(simulatedCard.serialNumber);

    if (!student) {
      throw new Error(ERROR_TYPES.CARD_NOT_FOUND);
    }

    // Simula leer datos de la tarjeta NFC
    // En la vida real, la tarjeta puede estar vacía inicialmente
    // Por ahora retornamos los datos del mockDataService
    const nfcData = {
      serialNumber: simulatedCard.serialNumber,
      estudianteId: student.id,
      nombre: student.nombre,
      saldo: student.saldoActual,
      grado: student.grado,
      ultimaTransaccion: student.ultimaTransaccion || null
    };

    console.log('✅ Datos leídos de la tarjeta NFC');

    if (onReading) {
      onReading(nfcData);
    }

    return { success: true, data: nfcData };
  }

  /**
   * Maneja la lectura de una tarjeta NFC real
   */
  handleReading(event, onReading) {
    try {
      const { message, serialNumber } = event;
      
      // Procesa los records del mensaje NDEF
      let nfcData = null;

      for (const record of message.records) {
        if (record.recordType === 'text') {
          const textDecoder = new TextDecoder(record.encoding || 'utf-8');
          const decodedText = textDecoder.decode(record.data);
          
          try {
            // Intenta desencriptar si está encriptado
            nfcData = encryptionService.decryptFromNFC(decodedText);
          } catch {
            // Si no está encriptado, parsea como JSON
            nfcData = JSON.parse(decodedText);
          }
        }
      }

      if (!nfcData) {
        throw new Error(ERROR_TYPES.DATA_CORRUPTED);
      }

      // Busca el estudiante en la base de datos
      const student = mockDataService.getStudentById(nfcData.estudianteId);

      if (!student) {
        throw new Error(ERROR_TYPES.CARD_NOT_FOUND);
      }

      // Combina datos de NFC con datos de la BD
      const completeData = {
        serialNumber,
        ...nfcData,
        nombre: student.nombre,
        grado: student.grado
      };

      console.log('✅ Datos NFC leídos correctamente');

      if (onReading) {
        onReading(completeData);
      }

      this.isScanning = false;
    } catch (error) {
      console.error('❌ Error procesando lectura NFC:', error);
      throw new Error(ERROR_TYPES.NFC_READ_ERROR);
    }
  }

  /**
   * Escribe datos en una tarjeta NFC
   */
  async write(data) {
    try {
      if (!this.isNFCSupported()) {
        throw new Error(ERROR_TYPES.NFC_NOT_SUPPORTED);
      }

      // Si estamos en modo simulador
      if (this.simulatorEnabled) {
        return await this.simulateWrite(data);
      }

      this.isWriting = true;

      // Encripta los datos
      const encryptedData = encryptionService.encryptForNFC(data);

      const ndef = new window.NDEFReader();
      
      // Necesitamos escanear primero para escribir
      await ndef.scan();

      // Escribe los datos
      await ndef.write({
        records: [
          {
            recordType: 'text',
            data: encryptedData
          }
        ]
      });

      console.log('✅ Datos escritos en NFC correctamente');
      this.isWriting = false;

      return { success: true };
    } catch (error) {
      console.error('❌ Error escribiendo en NFC:', error);
      this.isWriting = false;

      if (error.name === 'NotAllowedError') {
        throw new Error(ERROR_TYPES.NFC_PERMISSION_DENIED);
      }

      throw new Error(ERROR_TYPES.NFC_WRITE_ERROR);
    }
  }

  /**
   * Simula escritura NFC (para desarrollo)
   */
  async simulateWrite(data) {
    console.log('🔧 Simulando escritura NFC...');
    console.log('📝 Datos a escribir en la tarjeta:', {
      estudianteId: data.estudianteId,
      saldo: data.saldo || data.saldoActual,
      transaccion: data.transaccion
    });

    // Espera un poco para simular el proceso de escritura
    await sleep(1200);

    // Actualiza la tarjeta simulada
    const cardIndex = this.simulatedCards.findIndex(
      c => c.estudianteId === data.estudianteId
    );

    if (cardIndex !== -1) {
      this.simulatedCards[cardIndex].saldo = data.saldo || data.saldoActual;
      console.log('✅ Saldo actualizado en tarjeta NFC simulada');
      
      if (data.transaccion) {
        console.log('📝 Transacción escrita en tarjeta NFC:', {
          id: data.transaccion.id,
          tipo: data.transaccion.tipo,
          monto: data.transaccion.monto
        });
      }
    }

    // También actualiza en mockDataService
    mockDataService.updateStudent(data.estudianteId, {
      saldoActual: data.saldo || data.saldoActual,
      ultimaTransaccion: data.transaccion
    });

    console.log('✅ Escritura NFC completada exitosamente');
    return { success: true };
  }

  /**
   * Detiene el escaneo NFC
   */
  stopScan() {
    if (this.reader && this.isScanning) {
      // No hay método oficial para detener, pero podemos limpiar eventos
      this.reader.onreading = null;
      this.reader.onreadingerror = null;
      this.isScanning = false;
      console.log('⏹️ Escaneo NFC detenido');
    }
  }

  /**
   * Lee el saldo de una tarjeta NFC
   */
  async readBalance(serialNumber) {
    // En modo simulador
    if (this.simulatorEnabled) {
      const card = this.simulatedCards.find(c => c.serialNumber === serialNumber);
      if (card) {
        return card.saldo;
      }
    }

    // En producción, esto se manejaría con el callback de scan()
    throw new Error('Use scan() para leer datos NFC');
  }

  /**
   * Actualiza el saldo en una tarjeta NFC y escribe la transacción
   */
  async updateBalance(estudianteId, newBalance, transaccion = null) {
    const data = {
      estudianteId,
      saldo: newBalance,
      transaccion: transaccion,
      ultimaActualizacion: new Date().toISOString()
    };

    return await this.write(data);
  }

  /**
   * Escribe una transacción completa en la tarjeta NFC
   */
  async writeTransaction(transaction) {
    console.log('📝 Escribiendo transacción en tarjeta NFC:', transaction.id);
    
    const data = {
      estudianteId: transaction.estudianteId,
      saldo: transaction.saldoNuevo,
      transaccion: {
        id: transaction.id,
        tipo: transaction.tipo,
        monto: transaction.monto,
        fecha: transaction.fecha,
        vendedorId: transaction.vendedorId,
        productos: transaction.productos || null
      },
      ultimaActualizacion: new Date().toISOString()
    };

    return await this.write(data);
  }
}

// Exporta una instancia única (Singleton)
const nfcService = new NFCService();
export default nfcService;
